import {Badge} from '@common-web/components/ui/badge';
import {PayrollProcessingViewMode} from '@client/pages/payroll/PayrollProcessing';
import {Button} from '@common-web/components/ui/button';
import {History, Settings, LifeBuoy, Info, ArrowLeft, FileText} from 'lucide-react';
import {ConfiguratorGuide} from '@client/pages/payroll/components/ConfiguratorGuide';
import {memo, useState} from 'react';
import {ScrollArea} from '@common-web/components/ui/scroll-area';
import {SupportContactModal} from '@client/pages/payroll/components/SupportContactModal';
import clsx from 'clsx';
import {Link} from 'react-router-dom';

export const PayrollPageHeader = memo(
  ({
    groupName,
    isConfigured,
    viewMode,
    showHistory,
    setShowHistory,
    setViewMode,
    agentId,
    compact,
    theme = 'light',
  }: {
    groupName: string;
    isConfigured: boolean;
    viewMode: PayrollProcessingViewMode;
    showHistory: boolean;
    setShowHistory: (show: boolean) => void;
    setViewMode: (mode: PayrollProcessingViewMode) => void;
    agentId: string;
    compact?: boolean;
    theme?: 'light' | 'dark';
  }) => {
    const isProcessingFlow = viewMode === 'processing' || viewMode === 'results';
    const [showSupportModal, setShowSupportModal] = useState(false);

    return (
      <>
        {!compact && (
          <div className={'pt-6'}>
            <div className={'flex items-center justify-between gap-3'}>
              <h1 className='text-3xl font-bold'>Payroll Processing</h1>
              {viewMode !== 'results' && (
                <Badge
                  variant={isConfigured ? 'default' : 'secondary'}
                  className={
                    isConfigured
                      ? 'bg-emerald text-white border-0 flex-shrink-0 font-semibold px-3 py-1'
                      : 'bg-amber text-white border-0 flex-shrink-0 font-semibold px-3 py-1'
                  }
                >
                  {isConfigured ? 'Ready' : 'Setup Required'}
                </Badge>
              )}
            </div>
            <p className='text-muted-foreground mt-1'>
              Upload and process payroll files for {groupName}
            </p>
          </div>
        )}
        {!isProcessingFlow && (
          <ScrollArea orientation={'horizontal'} className={clsx('-mb-4')}>
            <div className='flex items-center gap-2 pb-4'>
              <Button
                variant='outline'
                size='sm'
                onClick={() => setShowSupportModal(true)}
                className={clsx(
                  theme === 'light'
                    ? 'bg-white hover:bg-white/80 hover:border-white/20 text-internal-brand-navy border-2 border-white/20 font-medium transition-all shadow-sm hover:shadow-md'
                    : 'border-internal-brand-navy text-internal-brand-navy hover:bg-internal-brand-navy hover:border-internal-brand-navy hover:text-white font-medium',
                )}
              >
                <LifeBuoy className='h-4 w-4 mr-2' />
                Contact Support
              </Button>
              <ConfiguratorGuide
                triggerButton={
                  <Button
                    variant='outline'
                    size='sm'
                    className={clsx(
                      theme === 'light'
                        ? 'bg-white hover:bg-white/80 hover:border-white/20 text-internal-brand-navy border-2 border-white/20 font-medium transition-all shadow-sm hover:shadow-md'
                        : 'border-internal-brand-navy text-internal-brand-navy hover:bg-internal-brand-navy hover:border-internal-brand-navy hover:text-white font-medium',
                    )}
                  >
                    <Info className='h-4 w-4 mr-2' />
                    Help & Parsing Guide
                  </Button>
                }
              />
              <Button
                variant='outline'
                size='sm'
                onClick={() => setShowHistory(!showHistory)}
                className={clsx(
                  theme === 'light'
                    ? 'bg-white hover:bg-white/80 hover:border-white/20 text-internal-brand-navy border-2 border-white/20 font-medium transition-all shadow-sm hover:shadow-md'
                    : 'border-internal-brand-navy text-internal-brand-navy hover:bg-internal-brand-navy hover:border-internal-brand-navy hover:text-white font-medium',
                )}
              >
                <History className='h-4 w-4 mr-2' />
                {showHistory ? 'Hide' : 'View'} History
              </Button>
              {isConfigured && (
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() =>
                    setViewMode(viewMode === 'configuration' ? 'upload' : 'configuration')
                  }
                  className={clsx(
                    theme === 'light'
                      ? 'bg-white hover:bg-white/80 hover:border-white/20 text-internal-brand-navy border-2 border-white/20 font-medium transition-all shadow-sm hover:shadow-md'
                      : 'border-internal-brand-navy text-internal-brand-navy hover:bg-internal-brand-navy hover:border-internal-brand-navy hover:text-white font-medium',
                  )}
                >
                  {viewMode === 'configuration' ? (
                    <ArrowLeft className='h-4 w-4 mr-2' />
                  ) : (
                    <Settings className='h-4 w-4 mr-2' />
                  )}
                  {viewMode === 'configuration' ? 'Back to Upload' : 'Configuration'}
                </Button>
              )}
            </div>
          </ScrollArea>
        )}
        <SupportContactModal
          isOpen={showSupportModal}
          onClose={() => setShowSupportModal(false)}
          agentId={agentId}
        />
      </>
    );
  },
);

export function PayrollUploadResultsHeader({
  isViewingFromHistory,
  onBackToHistory,
  onReset,
  theme = 'light',
}: {
  isViewingFromHistory: boolean;
  onBackToHistory?: () => void;
  onReset?: () => void;
  theme?: 'light' | 'dark';
}) {
  return (
    <div className='flex items-center gap-2 flex-wrap'>
      {isViewingFromHistory && !!onBackToHistory && (
        <Button
          onClick={onBackToHistory}
          variant='outline'
          size='sm'
          className={clsx(
            theme === 'light'
              ? 'bg-white hover:bg-white/80 hover:border-white/20 text-internal-brand-navy border-2 border-white/20 font-medium transition-all shadow-sm hover:shadow-md'
              : 'border-internal-brand-navy text-internal-brand-navy hover:bg-internal-brand-navy hover:border-internal-brand-navy hover:text-white font-medium',
          )}
        >
          <ArrowLeft className='h-4 w-4 mr-2' />
          Back
        </Button>
      )}
      {!!onReset && (
        <Button
          onClick={onReset}
          variant='outline'
          size='sm'
          className={clsx(
            theme === 'light'
              ? 'bg-white hover:bg-white/80 hover:border-white/20 text-internal-brand-navy border-2 border-white/20 font-medium transition-all shadow-sm hover:shadow-md'
              : 'border-internal-brand-navy text-internal-brand-navy hover:bg-internal-brand-navy hover:border-internal-brand-navy hover:text-white font-medium',
          )}
        >
          <FileText className='h-4 w-4 mr-2' />
          Upload New Files
        </Button>
      )}
      <Button
        asChild
        variant='outline'
        size='sm'
        className={clsx(
          theme === 'light'
            ? 'bg-white hover:bg-white/80 hover:border-white/20 text-internal-brand-navy border-2 border-white/20 font-medium transition-all shadow-sm hover:shadow-md'
            : 'border-internal-brand-navy text-internal-brand-navy hover:bg-internal-brand-navy hover:border-internal-brand-navy hover:text-white font-medium',
        )}
      >
        <Link to={'/groups'}>Return to Dashboard</Link>
      </Button>
    </div>
  );
}
